/**
 * React Hooks for Correspondence Exports
 *
 * This module provides custom hooks for managing correspondence export state and operations
 * using React Query with polling for async status updates.
 */

'use client';

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState
} from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { gatedAction } from '@/lib/api/auth-gate';
import {
  createCorrespondenceExportAction,
  getCorrespondenceExportStatusAction
} from './actions';
import {
  CORRESPONDENCE_EXPORT_KEYS,
  EXPORT_POLLING_INTERVAL_MS,
  EXPORT_POLLING_TIMEOUT_MS
} from '@/lib/constants';
import type {
  CreateCorrespondenceExportResponse,
  CorrespondenceExportStatusResponse,
  CorrespondenceExportStatus
} from './types';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook to fetch correspondence export status with polling
 * Automatically polls while status is 'processing'
 * Invalidates queries when status becomes 'failed'
 */
export function useCorrespondenceExportStatus(
  matterId: number,
  correspondenceId: number,
  exportId: number | null,
  options?: Omit<
    UseQueryOptions<CorrespondenceExportStatusResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const queryClient = useQueryClient();
  const previousStatusRef = useRef<CorrespondenceExportStatus | null>(null);

  const query = useQuery({
    queryKey: CORRESPONDENCE_EXPORT_KEYS.status(
      matterId,
      correspondenceId,
      exportId || 0
    ),
    queryFn: async () => {
      if (!exportId) throw new Error('Export ID is required');

      return gatedAction(() =>
        getCorrespondenceExportStatusAction(
          matterId,
          correspondenceId,
          exportId
        )
      );
    },
    enabled: !!matterId && !!correspondenceId && !!exportId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll only if status is 'processing'
      return data?.status === 'processing' ? EXPORT_POLLING_INTERVAL_MS : false;
    },
    refetchOnWindowFocus: true,
    staleTime: 0, // Always fetch fresh data when polling
    ...options
  });

  // Invalidate queries when status transitions to 'failed'
  useEffect(() => {
    const currentStatus = query.data?.status;

    if (currentStatus === 'failed' && previousStatusRef.current !== 'failed') {
      // Invalidate exports list to refresh the UI
      queryClient.invalidateQueries({
        queryKey: CORRESPONDENCE_EXPORT_KEYS.list(matterId, correspondenceId)
      });
    }

    previousStatusRef.current = currentStatus || null;
  }, [query.data?.status, matterId, correspondenceId, queryClient]);

  return query;
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

interface CreateCorrespondenceExportParams {
  matterId: number;
  correspondenceId: number;
}

/**
 * Hook to create a new correspondence export
 */
export function useCreateCorrespondenceExport(
  options?: UseMutationOptions<
    CreateCorrespondenceExportResponse,
    Error,
    CreateCorrespondenceExportParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      matterId,
      correspondenceId
    }: CreateCorrespondenceExportParams) => {
      const result = await createCorrespondenceExportAction(
        matterId,
        correspondenceId
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to create export');
      }

      return result.data!;
    },
    onSuccess: (data, { matterId, correspondenceId }) => {
      // Invalidate exports list to show the new export
      queryClient.invalidateQueries({
        queryKey: CORRESPONDENCE_EXPORT_KEYS.list(matterId, correspondenceId)
      });

      // Start polling for the new export status
      queryClient.invalidateQueries({
        queryKey: CORRESPONDENCE_EXPORT_KEYS.status(
          matterId,
          correspondenceId,
          data.id
        )
      });
    },
    ...options
  });
}

// ============================================================================
// COMBINED HOOKS
// ============================================================================

export type CorrespondenceExportProgress = {
  status: CorrespondenceExportStatus | 'idle';
  isCreating: boolean;
  isPolling: boolean;
  downloadUrl?: string | null;
  urlExpiresAt?: string | null;
  errorMessage?: string | null;
  fileCount?: number;
  totalSizeBytes?: number;
};

/**
 * Comprehensive hook for managing correspondence export lifecycle
 * Handles creation, polling, and download
 */
export function useCorrespondenceExport(
  matterId: number,
  correspondenceId: number | string
) {
  const queryClient = useQueryClient();
  const [currentExportId, setCurrentExportId] = useState<number | null>(null);
  const longDisplayToastIdRef = useRef<string | number | undefined>(undefined);
  const pollingStartTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Prevents "idle" glitch between createMutation and statusQuery states
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Ensure correspondenceId is a number
  const numericCorrespondenceId =
    typeof correspondenceId === 'string'
      ? parseInt(correspondenceId, 10)
      : correspondenceId;

  // Create export mutation
  const createMutation = useCreateCorrespondenceExport({
    onSuccess: (data) => {
      setCurrentExportId(data.id);

      // Start polling timeout
      pollingStartTimeRef.current = Date.now();

      // Set timeout to cancel polling after EXPORT_POLLING_TIMEOUT_MS
      timeoutRef.current = setTimeout(() => {
        // Cancel polling by resetting state
        setCurrentExportId(null);
        setIsProcessing(false);

        // Dismiss loading toast
        if (longDisplayToastIdRef.current) {
          toast.dismiss(longDisplayToastIdRef.current);
        }

        // Show timeout error
        toast.error('Download failed', {
          description:
            'The export is taking longer than expected. Please try again.'
        });
      }, EXPORT_POLLING_TIMEOUT_MS);
    },
    onError: () => {
      setIsProcessing(false);
      if (longDisplayToastIdRef.current) {
        toast.dismiss(longDisplayToastIdRef.current);
      }
      toast.error('Export failed', {
        description: 'The download failed. Please try again.'
      });
    }
  });

  // Poll export status
  const statusQuery = useCorrespondenceExportStatus(
    matterId,
    numericCorrespondenceId,
    currentExportId,
    {
      enabled: !!currentExportId
    }
  );

  // React to terminal export statuses ('failed' / 'completed') using the
  // "store info from previous renders" pattern — state resets happen in
  // render under an inequality guard; imperative side effects (toasts,
  // download) run from a small useEffect via useEffectEvent.
  const exportStatus = statusQuery.data?.status;
  const [prevExportStatus, setPrevExportStatus] =
    useState<typeof exportStatus>(undefined);
  if (exportStatus !== prevExportStatus) {
    setPrevExportStatus(exportStatus);
    if (exportStatus === 'failed') {
      setIsProcessing(false);
    } else if (exportStatus === 'completed') {
      const downloadUrl = statusQuery.data?.downloadUrl;
      const urlExpiresAt = statusQuery.data?.urlExpiresAt;
      // Runs only on the exportStatus transition render guarded above (store-
      // info-from-previous-renders pattern), not every render; reading the
      // wall clock here is safe.
      // eslint-disable-next-line @eslint-react/purity
      const isExpired = urlExpiresAt && new Date(urlExpiresAt) < new Date();
      if (!downloadUrl || isExpired) {
        setIsProcessing(false);
      } else {
        setIsProcessing(false);
        setCurrentExportId(null);
      }
    }
  }

  // Download export callback — declared before the terminal-status handler
  // so it's in scope when handleTerminalExportStatus dispatches the download.
  const downloadExport = useCallback(
    (downloadUrl: string) => {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `correspondence-${numericCorrespondenceId}-export.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (longDisplayToastIdRef.current) {
        toast.dismiss(longDisplayToastIdRef.current);
        longDisplayToastIdRef.current = undefined;
      }

      toast.success('Download started', {
        description: 'Your correspondence export is downloading'
      });
    },
    [numericCorrespondenceId]
  );

  const handleTerminalExportStatus = useEffectEvent(() => {
    if (exportStatus === 'failed') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (longDisplayToastIdRef.current) {
        toast.dismiss(longDisplayToastIdRef.current);
      }
      toast.error('Export failed', {
        description:
          statusQuery.data?.errorMessage ||
          'The download failed. Please try again.'
      });
      return;
    }
    if (exportStatus === 'completed' && statusQuery.data) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const { downloadUrl, urlExpiresAt } = statusQuery.data;
      const isExpired = urlExpiresAt && new Date(urlExpiresAt) < new Date();
      if (!downloadUrl || isExpired) {
        if (longDisplayToastIdRef.current) {
          toast.dismiss(longDisplayToastIdRef.current);
        }
        toast.error('Download link expired', {
          description: 'Please try downloading again.'
        });
        return;
      }
      downloadExport(downloadUrl);
    }
  });
  useEffect(() => {
    handleTerminalExportStatus();
  }, [exportStatus, statusQuery.data?.downloadUrl]);

  // Clean up toast and timeout on unmount
  useEffect(() => {
    return () => {
      if (longDisplayToastIdRef.current) {
        toast.dismiss(longDisplayToastIdRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Create export callback
  const createExport = useCallback(async () => {
    setIsProcessing(true);

    if (longDisplayToastIdRef.current) {
      toast.dismiss(longDisplayToastIdRef.current);
    }

    longDisplayToastIdRef.current = toast.loading('Preparing export', {
      description: `Gathering correspondence details...`,
      dismissible: false,
      duration: Infinity
    });

    try {
      await createMutation.mutateAsync({
        matterId,
        correspondenceId: numericCorrespondenceId
      });

      // Update toast message
      if (longDisplayToastIdRef.current) {
        toast.dismiss(longDisplayToastIdRef.current);
      }

      longDisplayToastIdRef.current = toast.loading('Preparing download', {
        description: `Packing correspondence files...`,
        dismissible: false,
        duration: Infinity
      });
    } catch (error) {
      setIsProcessing(false);
      console.error('Error creating correspondence export:', error);
      throw error;
    }
  }, [matterId, numericCorrespondenceId, createMutation]);

  // Reset export callback
  const resetExport = useCallback(() => {
    setCurrentExportId(null);
    setIsProcessing(false);
    queryClient.removeQueries({
      queryKey: CORRESPONDENCE_EXPORT_KEYS.status(
        matterId,
        numericCorrespondenceId,
        currentExportId || 0
      )
    });
  }, [matterId, numericCorrespondenceId, currentExportId, queryClient]);

  // Check if URL is expired
  const isUrlExpired = useCallback(
    (urlExpiresAt: string | null | undefined) => {
      if (!urlExpiresAt) return true;
      return new Date(urlExpiresAt) < new Date();
    },
    []
  );

  // Progress state
  const progress: CorrespondenceExportProgress = {
    status:
      statusQuery.data?.status ||
      (createMutation.isPending || isProcessing ? 'processing' : 'idle'),
    isCreating: createMutation.isPending,
    isPolling:
      statusQuery.isRefetching && statusQuery.data?.status === 'processing',
    downloadUrl: statusQuery.data?.downloadUrl,
    urlExpiresAt: statusQuery.data?.urlExpiresAt,
    errorMessage: statusQuery.data?.errorMessage,
    fileCount: statusQuery.data?.fileCount,
    totalSizeBytes: statusQuery.data?.totalSizeBytes
  };

  return {
    // Actions
    createExport,
    downloadExport,
    resetExport,

    // State
    progress,
    currentExportId,

    // Utilities
    isUrlExpired,

    // Query states
    isCreating: createMutation.isPending || isProcessing,
    isPolling: statusQuery.isFetching,
    isError: createMutation.isError || statusQuery.isError,
    error: createMutation.error || statusQuery.error
  };
}
